<template>
  <!-- _________________________ LOGIN _________________________ -->
  <div id="login-div">

    <h1 class="page-title login-title mt-4">
      {{ $t('loginTitle') }}
    </h1>
    <h2 class="secondary-page-title mt-2">
      {{ $t('loginSubtitle') }}
    </h2>

    <form @submit.stop.prevent="handleSubmit" class="mt-4">

      <b-alert :show="ui.showError" variant="danger">
        {{ ui.showErrorMessage }}
        <a href="#" v-on:click="resendValidateAccountMail" v-if="ui.showValidateAccountError">
          {{ $t('account.not.validated.resent.error') }}
        </a>
      </b-alert>

      <b-alert :show="ui.showResendValidateAccountMailSuccess" variant="success">
        {{ $t('ResendValidateAccountMail.success') }}
      </b-alert>

      <b-form-group
        :label="$t('loginFirstCredential')"
        label-for="username-input">
        <b-form-input
          id="username-input"
          v-model="form.username.value"
          type="email"
          required
          @keydown="hideError">
        </b-form-input>
      </b-form-group>

      <b-form-group
        :label="$t('loginSecondCredential')"
        label-for="password-input"
        class="pb-1">
        <div class="login-password-container">
          <b-form-input
            id="password-input"
            class="password-input-with-an-eye"
            v-model="form.password.value"
            :type="showPassword ? 'text' : 'password'"
            required
            @keydown="hideError">
          </b-form-input>
          <div class="login-show-password-btn" @click="showPassword = !showPassword">
            <i class="login-eye-btn ph-custom ph-eye-fill"/>
          </div>
        </div>
      </b-form-group>

      <div class="mt-4">
        <b-button type="submit" class="btn-block" variant="primary">{{ $t('LogInButtonText') }}</b-button>
      </div>

    </form>

    <div class="row justify-content-between">
      <div class="col">
        <b-form-checkbox
          class="mt-4 remember-me checkbox-label"
          id="remember_me"
          size="lg"
          v-model="form.rememberMe.value">
          {{ $t('Remember.me') }}
        </b-form-checkbox>
      </div>
      <div class=" col mt-4 text-right">
        <b-link v-on:click="showForgotPasswordView" class="forgot-password-link textual-link">
          {{ $t('forgotPasswordText') }}
        </b-link>
      </div>
    </div>

  </div>
</template>

<script>

import {mapActions} from 'vuex'
import api from '../../../services/api'
import TokenService from '../../../services/tokenService'

export default {

  name: 'LoginComponent',

  data() {
    return {
      showPassword: false,
      form: {
        username: {
          value: null
        },
        password: {
          value: null
        },
        rememberMe: {
          value: false
        }
      },
      ui: {
        showError: false,
        showErrorMessage: this.$i18n.t('internal.server.error'),
        showValidateAccountError: false,
        showResendValidateAccountMailSuccess: false
      }
    }
  },

  emits: ['showForgotPasswordView', 'showSmsCodeView'],

  mounted() {
  },

  methods: {

    ...mapActions(['LogIn', 'setRememberMe']),

    async handleSubmit() {
      let data = {
        username: this.form.username.value.trim(),
        password: this.form.password.value.trim()
      }

      this.setRememberMe(this.form.rememberMe.value)
      if (this.form.rememberMe.value) {
        data['remember-me'] = true
      }

      let formData = new FormData()
      formData.append('username', data['username'])
      formData.append('password', data['password'])

      await api({
        method: 'POST',
        url: '/authentication/beforeLogin',
        data: formData
      })
        .then(async (response) => {
          this.setRememberMe(this.form.rememberMe.value)
          let displaySmsCodeValidation = response.data.displayCodeValidation
          if (displaySmsCodeValidation) {
            // code sms
            let phoneNumber = Object(response.data)['phoneNumber']
            this.showSMSCodeView(data['username'], data['password'], phoneNumber)
          } else {
            // login
            await this.LogIn(data)
              .then(async () => {
                this.ui.showError = false
                this.goToHome()
              })
              .catch((error) => {
                if (error.toString() === 'Locked') {
                  this.ui.showErrorMessage = this.$i18n.t('Profile.forbidden.error')
                  this.ui.showError = true
                }
              })
          }
        })
        .catch((error) => {
          if (error.response.status === 401) {
            this.ui.showErrorMessage = this.$i18n.t('Login.error')
            this.ui.showError = true
          }
          if (error.response.status === 403) {
            this.ui.showErrorMessage = this.$i18n.t(error.response.data.message)
            this.ui.showError = true
            if (error.response.data.message === 'account.not.validated.error') {
              this.ui.showValidateAccountError = true
            }
          }
          if (error.response.status === 423) {
            this.ui.showErrorMessage = this.$i18n.t('account.locked.error')
            this.ui.showError = true
          }
          if (error.response.status === 500) {
            this.ui.showErrorMessage = this.$i18n.t('internal.server.error')
            this.ui.showError = true
          }
        })
    },

    setRememberMe(rememberMe) {
      TokenService.setLastActivityTime()
      TokenService.setRememberMe(rememberMe)
    },

    resendValidateAccountMail() {
      this.hideError()

      let formData = new FormData()
      formData.append('username', this.form.username.value.trim())

      api({
        method: 'POST',
        url: `/authentication/resendValidateAccountMail`,
        data: formData
      })
        .then(() => {
          this.ui.showResendValidateAccountMailSuccess = true
        })
        .catch((error) => {
          if (error.response.status === 401) {
            this.ui.showErrorMessage = this.$i18n.t('Login.error')
            this.ui.showError = true
          }
          if (error.response.status === 500) {
            this.ui.showErrorMessage = this.$i18n.t('internal.server.error')
            this.ui.showError = true
          }
        })
    },

    showForgotPasswordView() {
      this.$emit('showForgotPasswordView')
    },

    showSmsCodeView(username, password, phoneNumber) {
      this.$emit('showSmsCodeView', username, password, phoneNumber)
    },

    hideError() {
      this.ui.showError = false
      this.ui.showErrorMessage = null
      this.ui.showValidateAccountError = false
    },

    hideMessages() {
      this.ui.showResendValidateAccountMailSuccess = false
      this.hideError()
    },

    goToHome() {
      this.$router.push({
        name: 'FarmerIndex'
      })
    }

  }

}

</script>



// WEBPACK FOOTER //
// src/views/auth/components/LoginComponent.vue